import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { CollectionContact, CollectionInvoiceDocument } from '../model';
import OneOffEmailModal from './OneOffEmailModal';
import MandatoryEmailNoteModal from './MandatoryEmailNoteModal';
import {
  buildRecipientSet,
  contactIsSelected,
  sortContactsForDisplay,
  VISIBLE_CONTACT_LIMIT,
} from '../utils/sortContacts';
import { useCollectionConfig } from '../../config/hooks/useCollectionConfig';

interface Props {
  contacts: CollectionContact[];
  recipientEmails: string[];
  clientId: number;
  location: string;
  invoiceDocuments: CollectionInvoiceDocument[];
  onEmailSent: () => void;
  showActions?: boolean;
}

const toTelHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

const contactActionButtonSx = {
  width: 32,
  height: 32,
  p: 0,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'primary.dark',
  bgcolor: 'primary.main',
  color: 'common.white',
  flexShrink: 0,
  transition: 'transform 0.2s ease, background-color 0.2s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '1.125rem',
    transition: 'transform 0.2s ease',
  },
  '&:hover': {
    bgcolor: 'primary.dark',
    transform: 'scale(1.05)',
    '& .MuiSvgIcon-root': {
      transform: 'scaleX(-1)',
    },
  },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    color: 'action.disabled',
    borderColor: 'divider',
  },
} as const;

interface ContactActionButtonProps {
  title: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

const ContactActionButton: React.FC<ContactActionButtonProps> = ({ title, icon, disabled, onClick }) => (
  <Tooltip title={title}>
    <span>
      <IconButton size="small" disabled={disabled} onClick={onClick} aria-label={title} sx={contactActionButtonSx}>
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

interface ContactCardProps {
  contact: CollectionContact;
  recipientSet: Set<string>;
  onComposeEmail: (contact: CollectionContact, email: string) => void;
  showActions?: boolean;
}

const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  recipientSet,
  onComposeEmail,
  showActions = true,
}) => {
  const selected = contactIsSelected(contact, recipientSet);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        borderRadius: 1,
        p: 1.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {contact.name || '—'}
          </Typography>
          {contact.job_title && (
            <Typography variant="caption" color="text.secondary">
              {contact.job_title}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
          {contact.is_primary === 'A' && <Chip label="Primary" size="small" color="success" />}
          {contact.is_billing_primary === 'A' && <Chip label="Billing" size="small" color="primary" />}
          {contact.inactive && <Chip label="Inactive" size="small" variant="outlined" />}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
        {contact.phones.length > 0 ? (
          contact.phones.map((phone) => (
            <Box key={phone} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {showActions && (
                <ContactActionButton
                  title="Call via 3CX Click2Call"
                  icon={<PhoneIcon />}
                  onClick={() => {
                    window.location.href = toTelHref(phone);
                  }}
                />
              )}
              <Typography
                variant="body2"
                component="a"
                href={toTelHref(phone)}
                sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
              >
                {phone}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No phone
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75 }}>
        {contact.emails.length > 0 ? (
          contact.emails.map((email) => (
            <Box key={email} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {showActions && (
                <ContactActionButton
                  title={contact.con_id_no ? 'Send one-off email' : 'Contact has no Servman id'}
                  icon={<EmailIcon />}
                  disabled={!contact.con_id_no}
                  onClick={() => onComposeEmail(contact, email)}
                />
              )}
              <Chip label={email} size="small" />
            </Box>
          ))
        ) : (
          <Chip label="No email" size="small" color="error" variant="outlined" />
        )}
      </Box>
    </Box>
  );
};

const CollectionClientContactsCard: React.FC<Props> = ({
  contacts,
  recipientEmails,
  clientId,
  location,
  invoiceDocuments,
  onEmailSent,
  showActions = true,
}) => {
  const [composeTarget, setComposeTarget] = useState<{ email: string; name: string | null } | null>(null);
  const [noteContext, setNoteContext] = useState<{ toEmail: string; subject: string } | null>(null);
  const [showAllContacts, setShowAllContacts] = useState(false);

  const { data: configResp } = useCollectionConfig(location);
  const sendingDisabled = configResp?.data?.sending_enabled === false;

  useEffect(() => {
    setShowAllContacts(false);
  }, [clientId]);

  const sortedContacts = useMemo(() => sortContactsForDisplay(contacts, recipientEmails), [contacts, recipientEmails]);
  const visibleContacts = showAllContacts ? sortedContacts : sortedContacts.slice(0, VISIBLE_CONTACT_LIMIT);
  const hiddenContactCount = sortedContacts.length - VISIBLE_CONTACT_LIMIT;

  const recipientSet = buildRecipientSet(recipientEmails);

  const handleComposeEmail = (contact: CollectionContact, email: string) => {
    setComposeTarget({ email, name: contact.name });
  };

  if (!contacts || contacts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contacts.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={1.5}>
        {visibleContacts.map((contact, index) => (
          <Grid item xs={12} md={6} key={contact.con_id_no || `contact-${index}`}>
            <ContactCard
              contact={contact}
              recipientSet={recipientSet}
              onComposeEmail={handleComposeEmail}
              showActions={showActions}
            />
          </Grid>
        ))}
      </Grid>

      {sortedContacts.length > VISIBLE_CONTACT_LIMIT && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setShowAllContacts((prev) => !prev)}>
            {showAllContacts ? 'Show fewer contacts' : `Show more contacts (${hiddenContactCount} hidden)`}
          </Button>
        </Box>
      )}

      {composeTarget && (
        <OneOffEmailModal
          open={composeTarget !== null && noteContext === null}
          clientId={clientId}
          toEmail={composeTarget.email}
          contactName={composeTarget.name}
          sendingDisabled={sendingDisabled}
          invoiceDocuments={invoiceDocuments}
          onClose={() => setComposeTarget(null)}
          onSent={(info) => setNoteContext(info)}
        />
      )}

      {noteContext && (
        <MandatoryEmailNoteModal
          open={noteContext !== null}
          clientId={clientId}
          prefill={`One-off email sent to ${noteContext.toEmail} — Subject: ${noteContext.subject}`}
          onCompleted={() => {
            setNoteContext(null);
            setComposeTarget(null);
            onEmailSent();
          }}
        />
      )}
    </>
  );
};

export default CollectionClientContactsCard;
